import { Component, OnInit } from '@angular/core';
import { FacilityItemInformation } from 'src/app/common/models/facilityItemInformation';
import { MatDialog } from '@angular/material/dialog';
import { ItemsService } from 'src/app/services/items.service';
import { AuthManager } from 'src/app/core/auth/auth.manager';
import { FacilityManager } from 'src/app/managers/facility.manager';
import { ModifierAvailabilityComponent } from '../../components/modal/modifier-availability/modifier-availability.component';

import { ManageItemFilterComponent } from '../../components/manage-item-filter/manage-item-filter.component';
import { MatSlideToggle } from '@angular/material/slide-toggle';
import { FormsModule } from '@angular/forms';

@Component({
  selector: 'app-manage-modifiers',
  templateUrl: './manage-modifiers.component.html',
  styleUrls: ['./manage-modifiers.component.scss'],
  standalone: true,
  imports: [ManageItemFilterComponent, MatSlideToggle, FormsModule],
})
export class ManageModifiersComponent implements OnInit {
  public currentFacilityId: string | any;
  public menuModifiers: FacilityItemInformation[] = [];
  public filteredModifiers: FacilityItemInformation[] = [];
  public duplicateModifiers: any = {};
  public modifiersToFilter: FacilityItemInformation[] = [];
  public loadingPoll = 0;
  public updatingData = false;
  public setup = false;

  constructor(
    private _facilityManager: FacilityManager,
    private itemService: ItemsService,
    private auth: AuthManager,
    public dialog: MatDialog,
  ) {
    this.currentFacilityId = this._facilityManager.getCurrentFacility();
  }

  public ngOnInit(): void {
    this.auth.isLoggedIn$.subscribe((isAuthed) => {
      if (isAuthed) {
        this._facilityManager.facilityIdUpdates$.subscribe((fId) => {
          if (this.currentFacilityId !== fId || !this.setup) {
            this.setup = true;
            this.currentFacilityId = fId;
            this.refreshData(fId);
          }
        });
      }
    });
  }

  private refreshData(facilityId: string): void {
    this.updatingData = true;
    this.itemService.getModifiers(facilityId).subscribe((data) => {
      this.menuModifiers = data;
      this.condenseDuplicateModifiersForDepartment(data);
      this.filteredModifiers = JSON.parse(JSON.stringify(this.modifiersToFilter));
      this.loadingPollCalculation();
    });
  }

  private condenseDuplicateModifiersForDepartment(data: FacilityItemInformation[]): void {
    this.modifiersToFilter = data;
    this.menuModifiers.forEach((m) => {
      if (m.itemName !== 'undefined' && m.productType !== 'undefined') {
        this.filterByNameAndProductType(m.itemName, m.productType);
      }
    });

    this.addDuplicatesToModifierArray();
  }

  private filterByNameAndProductType(itemName: string, productType: string): void {
    const nameAndProductTypeArray = this.modifiersToFilter.filter(
      (m) => m.itemName === itemName && m.productType === productType,
    );

    if (nameAndProductTypeArray.length > 1) {
      this.duplicateModifiers[productType + itemName] = nameAndProductTypeArray;

      this.modifiersToFilter = this.modifiersToFilter.filter((m) => {
        if (m.itemName === itemName && m.productType === productType) {
          return false;
        }
        return true;
      });
    }
  }

  private addDuplicatesToModifierArray(): void {
    for (const npt in this.duplicateModifiers) {
      const currentModifier = this.duplicateModifiers[npt][0];

      const filtered: FacilityItemInformation = {
        commerceItemNumber: currentModifier.commerceItemNumber,
        externalItemNumber: currentModifier.externalItemNumber,
        departmentName: '',
        facilityId: '',
        duplicateModifiers: this.duplicateModifiers[npt].length,
        duplicateCommerceItemNumbers: this.duplicateModifiers[npt].reduce(function (
          result: string[],
          test: FacilityItemInformation,
        ) {
          if (test.commerceItemNumber !== currentModifier.commerceItemNumber) {
            result.push(test.commerceItemNumber);
          }
          return result;
        }, []),
        isAvailable: !this.duplicateModifiers[npt].some((m: { isAvailable: any }) => m.isAvailable === false),
        isInventoryManagementException: false,
        itemDescription: currentModifier.itemName,
        itemName: currentModifier.itemName,
        leadMinutes: 0,
        prepMinutes: 0,
        postPrepMinutes: 0,
        productType: currentModifier.productType,
        sellableQuantity: currentModifier.sellableQuantity,
        isSellableQuantityEnabled: currentModifier.isSellableQuantityEnabled,
        soldQuantity: currentModifier.soldQuantity,
      };

      this.modifiersToFilter.push(filtered);
      this.modifiersToFilter.sort((a, b) => (a.itemName > b.itemName ? 1 : -1));
    }
  }

  private loadingPollCalculation(): void {
    this.loadingPoll++;
    if (this.loadingPoll === 1) {
      setTimeout(() => {
        this.updatingData = false;
      }, 500);
      this.loadingPoll = 0;
    }
  }

  public updateFilteredItems(modifiers: FacilityItemInformation[]): void {
    // received from event emitter in child component
    this.filteredModifiers = modifiers;
  }

  public openAvailabilityDialog(item: FacilityItemInformation): void {
    const dialogRef = this.dialog.open(ModifierAvailabilityComponent, {
      maxHeight: '755px',
      maxWidth: '1200px',
      width: '1000px',
      data: item,
    });

    dialogRef.componentInstance.isModifier = true;

    if ('duplicateCommerceItemNumbers' in item) {
      item.duplicateCommerceItemNumbers.forEach((element) => {
        dialogRef.componentInstance.additionalItems.push(element);
      });
    }

    dialogRef.afterClosed().subscribe((result: FacilityItemInformation[] | null) => {
      if (result === null) {
        item.isAvailable = !item.isAvailable;
      } else {
        result.forEach((r: FacilityItemInformation) => {
          this.updateModifiersArray(this.modifiersToFilter, r);
          this.updateModifiersArray(this.filteredModifiers, r);
        });
      }
    });
  }

  private updateModifiersArray(items: FacilityItemInformation[], updatedItem: FacilityItemInformation): void {
    const index = this.findModifierIndexByCommerceItemNumber(items, updatedItem.commerceItemNumber);
    //if we find the item in the array
    if (index > -1) {
      const replacement = JSON.parse(JSON.stringify(items[index]));
      replacement.isAvailable = updatedItem.isAvailable;
      //if the filter is active, we want to trigger a filterChangeUpdate
      items.splice(index, 1);
      items.splice(index, 0, replacement);
    }
  }

  private findModifierIndexByCommerceItemNumber(items: FacilityItemInformation[], commerceItemNumber: string): number {
    return items.findIndex((i) => {
      return i.commerceItemNumber === commerceItemNumber;
    });
  }

  public displayCommerceItemNumber(item: FacilityItemInformation): string {
    if (item.duplicateModifiers > 0) {
      return '';
    }
    return '(#' + item.commerceItemNumber + ')';
  }
}
